/**
 * The calibration wizard — gesture-vocabulary.md §Calibration, built.
 *
 * Four guided steps, each: instruction → wait for a visible expression hand
 * ("show your hand" until then) → sample raw measures for ~3s with a progress
 * bar → advance. All decisions about what the samples MEAN live in
 * mapping/calibration.ts (pure); this file owns only collection, the overlay,
 * and the one localStorage write.
 *
 * Skip is always visible and always safe: it applies whatever was in force
 * before (stored calibration or defaults) and closes.
 */

import { CALIBRATION_KEY, fitRanges, serializeCalibration, type CalibrationSamples } from '../mapping/calibration';
import type { Ranges } from '../mapping/features';
import { rawMeasures } from '../mapping/features';
import { assignRoles, type RawHand } from '../vision/landmarks';
import { button, el } from './controls';

interface Step {
  key: keyof CalibrationSamples;
  title: string;
  tell: string;
}

const STEPS: Step[] = [
  { key: 'aperture', title: 'Pinch', tell: 'Open and close your pinch three times — thumb and index finger.' },
  { key: 'spread', title: 'Spread', tell: 'Spread your fingers wide, then relax them. Twice is plenty.' },
  { key: 'height', title: 'Reach', tell: 'Reach as high as you comfortably play, then as low.' },
  { key: 'rest', title: 'Rest', tell: 'Drop your hands and relax. This sets where silence lives.' },
];

const SAMPLE_MS = 3000;
const SAMPLE_HZ = 30;

export interface CalibrateDeps {
  latestHands(): readonly RawHand[];
  swapRoles?: boolean;
  applyRanges(r: Ranges): void;
  /** Called with a human summary when the wizard closes, either way. */
  done(status: string): void;
}

export function loadStoredCalibration(): string | null {
  try { return localStorage.getItem(CALIBRATION_KEY); } catch { return null; }
}

function storeCalibration(ranges: Ranges): boolean {
  try { localStorage.setItem(CALIBRATION_KEY, serializeCalibration(ranges)); return true; }
  catch { return false; } // private mode — session-only, and we say so
}

export function openCalibration(deps: CalibrateDeps): HTMLElement {
  const samples: CalibrationSamples = { aperture: [], spread: [], height: [], rest: [] };
  let stepIx = 0;
  let timer = 0;
  let started = 0;

  const title = el('h1', {}, [STEPS[0].title]);
  const tell = el('p', { class: 'lede' }, [STEPS[0].tell]);
  const state = el('p', { class: 'note' }, ['Show your hand to the camera to begin.']);
  const barFill = el('i', { class: 'cal-fill' });
  const bar = el('div', { class: 'cal-bar', 'aria-hidden': 'true' }, [barFill]);
  const stepDots = el('p', { class: 'note dim' }, [`step 1 of ${STEPS.length}`]);

  const skip = button('Skip', () => close('calibration skipped — previous ranges kept.'), 'ghost');

  const overlay = el('div', { class: 'gate', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Calibration' }, [
    el('div', { class: 'gate-card' }, [
      el('p', { class: 'slabel' }, ['Calibration']),
      title, tell, bar, state, stepDots, skip,
    ]),
  ]);

  function exprHand(): RawHand | null {
    const hands = deps.latestHands();
    return assignRoles(hands, deps.swapRoles ?? false).expr;
  }

  function tick(): void {
    const hand = exprHand();
    if (!hand) {
      // Timer pauses while tracking is lost mid-step — a dropout must not eat
      // the sampling window and calibrate from half a gesture.
      started = 0;
      state.textContent = 'Show your hand to the camera…';
      barFill.style.width = '0%';
      return;
    }
    const raw = rawMeasures({ points: hand.points, handedness: 'right', score: hand.score });
    if (!raw) return;
    if (!started) started = performance.now();

    const key = STEPS[stepIx].key;
    samples[key].push(key === 'rest' || key === 'height' ? raw.height : raw[key as 'aperture' | 'spread']);
    // Height and rest both sample the height axis; aperture/spread their own.

    const t = performance.now() - started;
    barFill.style.width = `${Math.min(100, (t / SAMPLE_MS) * 100)}%`;
    state.textContent = 'Sampling…';
    if (t >= SAMPLE_MS) advance();
  }

  function advance(): void {
    stepIx++;
    started = 0;
    if (stepIx >= STEPS.length) { finish(); return; }
    title.textContent = STEPS[stepIx].title;
    tell.textContent = STEPS[stepIx].tell;
    stepDots.textContent = `step ${stepIx + 1} of ${STEPS.length}`;
    barFill.style.width = '0%';
  }

  function finish(): void {
    const { ranges, issues } = fitRanges(samples);
    deps.applyRanges(ranges);
    const stored = storeCalibration(ranges);
    const bits = ['calibrated'];
    if (!stored) bits.push('not saved (storage unavailable) — this session only');
    bits.push(...issues);
    close(bits.join(' · '));
  }

  function close(status: string): void {
    window.clearInterval(timer);
    overlay.remove();
    deps.done(status);
  }

  timer = window.setInterval(tick, 1000 / SAMPLE_HZ);
  return overlay;
}
